import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

// Slim, flat category strip (replaces the gradient CategoriesSlider on /shop).
// Solid colours only; the selected chip is solid green.

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #eef1f2;
  border-radius: 10px;
  padding: 8px 10px;
  box-shadow: 0 1px 4px rgba(20, 40, 45, 0.06);
`;
const Track = styled.div`
  display: flex;
  gap: 7px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  flex: 1;
`;
const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  border: 1px solid ${(p) => (p.$active ? "#03660c" : "#dfe5e7")};
  background: ${(p) => (p.$active ? "#03660c" : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "#33454b")};
  font-size: 13px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
  &:hover {
    border-color: #03660c;
    color: ${(p) => (p.$active ? "#fff" : "#03660c")};
  }
`;
const Arrow = styled.button`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #dfe5e7;
  background: #fff;
  color: #33454b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { border-color: #03660c; color: #03660c; }
`;

const Chevron = ({ dir }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {dir === "left" ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
  </svg>
);

// Tick shown inside the ACTIVE chip only — the solid green background alone
// reads as "highlighted", the tick reads as "chosen".
const Tick = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CategoryStrip = ({ categories = [], selected, onSelect }) => {
  const track = useRef(null);
  const activeRef = useRef(null);
  const scroll = (dx) => track.current && track.current.scrollBy({ left: dx, behavior: "smooth" });

  // "All" is provided by the reset chip below, so drop any backend category
  // literally named "All" to avoid a duplicate.
  const cats = (categories || []).filter(
    (c) => (c.category || "").toLowerCase() !== "all"
  );

  // Snap the selected chip into view when it changes.
  useEffect(() => {
    if (!selected) {
      if (track.current) track.current.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    if (activeRef.current) {
      activeRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [selected]);

  return (
    <Bar>
      <Arrow onClick={() => scroll(-240)} aria-label="Scroll left"><Chevron dir="left" /></Arrow>
      <Track ref={track}>
        <Chip $active={!selected} onClick={() => onSelect("")}>
          {!selected && <Tick />}
          All
        </Chip>
        {cats.map((c) => (
          <Chip
            key={c._id}
            ref={selected === c.category ? activeRef : null}
            $active={selected === c.category}
            onClick={() => onSelect(selected === c.category ? "" : c.category)}
          >
            {selected === c.category && <Tick />}
            {c.category}
          </Chip>
        ))}
      </Track>
      <Arrow onClick={() => scroll(240)} aria-label="Scroll right"><Chevron dir="right" /></Arrow>
    </Bar>
  );
};

CategoryStrip.propTypes = {
  categories: PropTypes.array,
  selected: PropTypes.string,
  onSelect: PropTypes.func,
};

export default CategoryStrip;
